(() => {
  const root = document.documentElement;
  root.classList.remove('no-js');
  root.classList.add('js');

  const drawerApi = new Map();
  const drawers = document.querySelectorAll('[data-reign-drawer]');
  drawers.forEach((drawer) => {
    const drawerId = drawer.getAttribute('id');
    const toggle = drawerId
      ? document.querySelector(`[data-reign-drawer-toggle][aria-controls="${drawerId}"]`)
      : null;
    const closeButtons = drawer.querySelectorAll('[data-reign-drawer-close]');
    let closeTimer;
    let returnFocus;

    const openDrawer = () => {
      window.clearTimeout(closeTimer);
      returnFocus = document.activeElement;
      drawer.hidden = false;
      drawer.classList.add('is-active');
      document.body.classList.add('reign-drawer-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      drawer.querySelector('button[data-reign-drawer-close]')?.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('reign:drawer:change'));
    };

    const closeDrawer = () => {
      drawer.classList.remove('is-active');
      document.body.classList.remove('reign-drawer-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('reign:drawer:change'));
      closeTimer = window.setTimeout(() => {
        drawer.hidden = true;
      }, 200);
    };

    if (drawerId) {
      drawerApi.set(drawerId, { open: openDrawer, close: closeDrawer, drawer });
    }

    if (toggle) {
      toggle.addEventListener('click', () => {
        if (drawer.classList.contains('is-active')) {
          closeDrawer();
        } else {
          openDrawer();
        }
      });
    }

    closeButtons.forEach((button) => {
      button.addEventListener('click', closeDrawer);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Tab' && drawer.classList.contains('is-active')) {
        const focusable = Array.from(drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]'))
          .filter((element) => element.getClientRects().length);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
      if (event.key === 'Escape' && drawer.classList.contains('is-active')) {
        closeDrawer();
      }
    });
  });

  const openDrawerById = (id) => {
    const api = drawerApi.get(id);
    if (api) {
      api.open();
    }
  };

  const closeDrawerById = (id) => {
    const api = drawerApi.get(id);
    if (api) {
      api.close();
    }
  };

  const modalApi = new Map();
  let activeModal = null;

  const initModals = (scope = document) => {
    const modals = [];
    if (scope && scope.matches && scope.matches('[data-reign-modal]')) {
      modals.push(scope);
    }
    if (scope && scope.querySelectorAll) {
      modals.push(...scope.querySelectorAll('[data-reign-modal]'));
    }

    modals.forEach((modal) => {
      const modalId = modal.getAttribute('id');
      if (!modalId || modal.dataset.reignModalInit === 'true') return;
      modal.dataset.reignModalInit = 'true';
      const closeButtons = modal.querySelectorAll('[data-reign-modal-close]');

      const open = (trigger) => {
        modal.hidden = false;
        modal.classList.add('is-active');
        modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('reign-modal-open');
        activeModal = modal;

        const mediaId = trigger ? trigger.dataset.reignModalMedia : null;
        if (mediaId) {
          const items = modal.querySelectorAll('[data-reign-modal-media-item]');
          items.forEach((item) => {
            item.classList.toggle('is-active', item.dataset.mediaId === String(mediaId));
          });
        }
      };

      const close = () => {
        modal.classList.remove('is-active');
        modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('reign-modal-open');
        if (activeModal === modal) {
          activeModal = null;
        }
        window.setTimeout(() => {
          modal.hidden = true;
        }, 200);
      };

      modalApi.set(modalId, { open, close, modal });

      closeButtons.forEach((button) => {
        button.addEventListener('click', close);
      });
    });
  };

  const openModalById = (id, trigger) => {
    if (!id) return;
    if (!modalApi.has(id)) {
      const modal = document.getElementById(id);
      if (modal) {
        initModals(modal);
      }
    }
    const api = modalApi.get(id);
    if (api) {
      api.open(trigger);
    }
  };

  const closeModalById = (id) => {
    const api = modalApi.get(id);
    if (api) {
      api.close();
    }
  };

  const initModalTriggers = (scope = document) => {
    const triggers = [];
    if (scope && scope.matches && scope.matches('[data-reign-modal-open]')) {
      triggers.push(scope);
    }
    if (scope && scope.querySelectorAll) {
      triggers.push(...scope.querySelectorAll('[data-reign-modal-open]'));
    }
    triggers.forEach((trigger) => {
      if (trigger.dataset.reignModalBound) return;
      trigger.dataset.reignModalBound = 'true';
      trigger.addEventListener('click', () => {
        const id = trigger.dataset.reignModalOpen;
        if (id) {
          openModalById(id, trigger);
        }
      });
    });
  };

  initModals();
  initModalTriggers();

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeModal) {
      const id = activeModal.getAttribute('id');
      if (id) {
        closeModalById(id);
      }
    }
  });

  const headers = document.querySelectorAll('[data-sticky]');
  headers.forEach((header) => {
    let lastScroll = window.scrollY;
    let ticking = false;
    const mode = header.dataset.sticky || 'none';
    const threshold = 40;

    const updateHeader = () => {
      const current = window.scrollY;
      const scrolled = current > threshold;
      header.classList.toggle('reign-header--scrolled', scrolled);

      if (mode === 'shrink') {
        header.classList.toggle('reign-header--compact', scrolled);
      }

      if (mode === 'on-scroll-up') {
        const shouldHide = current > lastScroll && current > threshold;
        header.classList.toggle('reign-header--hidden', shouldHide);
      }

      lastScroll = current;
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(updateHeader);
        ticking = true;
      }
    });
  });

  const megaMenus = document.querySelectorAll('[data-reign-mega]');
  megaMenus.forEach((menu) => {
    const tabs = menu.querySelectorAll('[data-reign-mega-tab]');
    const panels = menu.querySelectorAll('[data-reign-mega-panel]');

    const activate = (id) => {
      tabs.forEach((tab) => {
        const isActive = tab.dataset.reignMegaTab === id;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      panels.forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.reignMegaPanel === id);
      });
    };

    tabs.forEach((tab) => {
      tab.addEventListener('mouseenter', () => activate(tab.dataset.reignMegaTab));
      tab.addEventListener('focus', () => activate(tab.dataset.reignMegaTab));
    });
  });

  const initPredictiveSearch = (scope = document) => {
    const nodes = [];
    if (scope && scope.matches && scope.matches('[data-reign-predictive-search]')) {
      nodes.push(scope);
    }
    if (scope && scope.querySelectorAll) {
      nodes.push(...scope.querySelectorAll('[data-reign-predictive-search]'));
    }

    nodes.forEach((container) => {
      if (!container || container.dataset.reignPredictiveInit === 'true') return;
      container.dataset.reignPredictiveInit = 'true';

      const details = container.matches('details') ? container : container.closest('details');
      const form = container.querySelector('.reign-search__form');
      const input = container.querySelector('input[type="search"][name="q"]');
      const results = container.querySelector('[data-reign-predictive-results]');
      const loading = container.querySelector('[data-reign-predictive-loading]');
      if (!form || !input || !results) return;

      const endpointFromData = document.body?.dataset?.reignPredictiveSearch || '';
      const rootPath = window.Shopify?.routes?.root || '/';
      const endpointBase = endpointFromData || `${rootPath}search/suggest`;

      let activeRequest = null;
      let debounceTimer = null;
      let activeIndex = -1;
      let optionUid = 0;

      const setExpanded = (expanded) => {
        input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      };

      const getResultLinks = () => {
        const links = Array.from(results.querySelectorAll('.reign-search__predictive-link'));
        links.forEach((link) => {
          if (!link.id) {
            optionUid += 1;
            link.id = `ReignPredictiveOption-${optionUid}`;
          }
        });
        return links;
      };

      const setActiveResult = (index) => {
        const links = getResultLinks();
        if (!links.length) {
          activeIndex = -1;
          input.removeAttribute('aria-activedescendant');
          return;
        }
        if (index < 0 || index >= links.length) {
          activeIndex = -1;
          links.forEach((link) => link.classList.remove('is-active'));
          input.removeAttribute('aria-activedescendant');
          return;
        }

        activeIndex = index;
        links.forEach((link, linkIndex) => {
          link.classList.toggle('is-active', linkIndex === activeIndex);
        });
        const activeLink = links[activeIndex];
        if (activeLink) {
          input.setAttribute('aria-activedescendant', activeLink.id);
          activeLink.scrollIntoView({ block: 'nearest' });
        }
      };

      const hideLoading = () => {
        if (loading) loading.hidden = true;
      };

      const showLoading = () => {
        if (loading) loading.hidden = false;
      };

      const closeResults = () => {
        results.hidden = true;
        results.innerHTML = '';
        activeIndex = -1;
        input.removeAttribute('aria-activedescendant');
        hideLoading();
        setExpanded(false);
      };

      const fetchResults = async (term) => {
        if (!term || term.length < 2) {
          closeResults();
          return;
        }

        if (activeRequest) {
          activeRequest.abort();
        }
        activeRequest = new AbortController();

        showLoading();
        const params = new URLSearchParams();
        params.set('q', term);
        params.set('resources[type]', 'product,page,article');
        params.set('resources[limit]', '4');
        params.set('resources[limit_scope]', 'each');
        params.set('resources[options][unavailable_products]', 'last');
        params.set('section_id', 'predictive-search');

        try {
          const response = await fetch(`${endpointBase}?${params.toString()}`, {
            signal: activeRequest.signal,
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });
          const html = await response.text();
          if (!response.ok) {
            throw new Error(`Predictive search failed (${response.status})`);
          }
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const section = doc.querySelector('#shopify-section-predictive-search');
          const markup = section ? section.innerHTML.trim() : '';
          if (!markup) {
            closeResults();
            return;
          }
          results.innerHTML = markup;
          results.hidden = false;
          activeIndex = -1;
          input.removeAttribute('aria-activedescendant');
          setExpanded(true);
        } catch (error) {
          if (error && error.name === 'AbortError') return;
          closeResults();
        } finally {
          hideLoading();
        }
      };

      input.addEventListener('input', () => {
        const term = input.value.trim();
        window.clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(() => {
          fetchResults(term);
        }, 220);
      });

      input.addEventListener('focus', () => {
        const term = input.value.trim();
        if (term.length >= 2) {
          fetchResults(term);
        }
      });

      input.addEventListener('keydown', (event) => {
        const links = getResultLinks();
        if (event.key === 'ArrowDown') {
          if (!links.length) return;
          event.preventDefault();
          const nextIndex = activeIndex < links.length - 1 ? activeIndex + 1 : 0;
          setActiveResult(nextIndex);
          return;
        }

        if (event.key === 'ArrowUp') {
          if (!links.length) return;
          event.preventDefault();
          const prevIndex = activeIndex > 0 ? activeIndex - 1 : links.length - 1;
          setActiveResult(prevIndex);
          return;
        }

        if (event.key === 'Enter') {
          if (activeIndex >= 0 && links[activeIndex]) {
            event.preventDefault();
            window.location.href = links[activeIndex].href;
          }
          return;
        }

        if (event.key === 'Escape') {
          closeResults();
          if (details && details.open) {
            details.removeAttribute('open');
          }
          return;
        }
      });

      form.addEventListener('submit', () => {
        closeResults();
      });

      results.addEventListener('mousemove', (event) => {
        const hoveredLink = event.target.closest('.reign-search__predictive-link');
        if (!hoveredLink || !results.contains(hoveredLink)) return;
        const links = getResultLinks();
        const hoveredIndex = links.indexOf(hoveredLink);
        if (hoveredIndex >= 0) {
          setActiveResult(hoveredIndex);
        }
      });

      if (details) {
        details.addEventListener('toggle', () => {
          if (!details.open) {
            closeResults();
          }
        });
      }

      document.addEventListener('click', (event) => {
        if (!container.contains(event.target)) {
          closeResults();
        }
      });
    });
  };

  const formatWithDelimiters = (number, precision, thousands, decimal) => {
    if (isNaN(number) || number === null) return '0';
    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  };

  const formatMoney = (cents, format) => {
    const presentment = window.ReignMoney?.formatPresentment(cents);
    if (presentment != null) return presentment;
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    const formatString = format || '${{amount}}';
    const placeholderRegex = /{{\s*(\w+)\s*}}/;
    const match = formatString.match(placeholderRegex);
    if (!match) return formatString;
    const value = Number(cents) || 0;

    let formatted;
    switch (match[1]) {
      case 'amount':
        formatted = formatWithDelimiters(value, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        formatted = formatWithDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = formatWithDelimiters(value, 0, '.', ',');
        break;
      default:
        formatted = formatWithDelimiters(value, 2, ',', '.');
    }

    return formatString.replace(placeholderRegex, formatted);
  };

  const initProductSection = (section) => {
    if (!section || section.dataset.reignProductInit === 'true') return;
    const variantsScript = section.querySelector('[data-reign-variants]');
    if (!variantsScript) return;
    section.dataset.reignProductInit = 'true';

    let variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (error) {
      return;
    }

    if (!variants.length) return;

    const optionInputs = section.querySelectorAll('[data-reign-option]');
    const stylePickers = section.querySelectorAll('[data-reign-style-picker]');
    const productForm = section.querySelector('[data-reign-product-form]');
    const variantInput = section.querySelector('[data-reign-variant-input]');
    const installmentInput = section.querySelector('[data-reign-installment-id]');
    const addButton = section.querySelector('[data-reign-atc]');
    const stickyBar = section.querySelector('[data-reign-sticky-atc]');
    const stickyPrice = stickyBar ? stickyBar.querySelector('[data-reign-sticky-price]') : null;
    const stickyVariant = stickyBar ? stickyBar.querySelector('[data-reign-sticky-variant]') : null;
    const stickyButton = stickyBar ? stickyBar.querySelector('[data-reign-sticky-submit]') : null;
    const stickyFormId = stickyBar ? stickyBar.dataset.reignStickyForm : null;
    const stickyForm = stickyFormId ? document.getElementById(stickyFormId) : null;
    const priceCurrent = section.querySelector('[data-reign-current]');
    const priceCompare = section.querySelector('[data-reign-compare]');
    const priceWrap = section.querySelector('[data-reign-price]');
    const skuEl = section.querySelector('[data-reign-sku]');
    const inventoryEl = section.querySelector('[data-reign-inventory]');
    const inventoryText = inventoryEl ? inventoryEl.querySelector('[data-reign-inventory-text]') : null;
    const moneyFormat = section.dataset.moneyFormat || '${{amount}}';
    const mediaMain = section.querySelector('.reign-product__media-main');
    const mediaTrack = section.querySelector('[data-reign-media-track]') || mediaMain;
    const mediaItems = section.querySelectorAll('.reign-product__media-item');
    const mediaThumbs = section.querySelectorAll('[data-reign-media-thumb]');
    const modalItems = section.querySelectorAll('[data-reign-modal-media-item]');
    const mediaPrev = section.querySelector('[data-reign-media-prev]');
    const mediaNext = section.querySelector('[data-reign-media-next]');
    const qtyInput = section.querySelector('[data-reign-qty-input]');
    const qtyButtons = section.querySelectorAll('[data-reign-qty-step]');
    const qtyBreaks = section.querySelectorAll('[data-reign-qty-break]');
    const mediaOrder = Array.from(mediaItems)
      .map((item) => item.dataset.mediaId)
      .filter(Boolean);
    let activeVariant = null;
    let duoOffer = null;

    const getSelectedOptions = () => {
      const selections = [];
      optionInputs.forEach((input) => {
        if (input.checked) {
          const index = Number(input.dataset.optionIndex);
          selections[index] = input.value;
        }
      });
      return selections;
    };

    const formatStylePickerLabel = (template, count) =>
      (template || '').replace(/\[count\]/g, String(count));

    const syncStylePicker = (picker) => {
      if (!picker) return;
      const choices = Array.from(picker.querySelectorAll('[data-reign-style-choice]'));
      const selectedInput = picker.querySelector('[data-reign-option]:checked');
      const selectedLabel = picker.querySelector('[data-reign-selected-option]');
      const countText = picker.querySelector('[data-reign-style-count-text]');
      const toggle = picker.querySelector('[data-reign-style-toggle]');
      const expanded = picker.classList.contains('is-expanded');
      const total = Number(picker.dataset.styleCount || choices.length);
      const initial = Math.min(Number(picker.dataset.styleVisibleCount || total), total);
      let priorityCount = 0;

      choices.forEach((choice) => {
        const input = choice.querySelector('[data-reign-option]');
        const selected = Boolean(input && input.checked);
        const priority = !expanded && selected && choice.classList.contains('is-beyond-limit');
        choice.classList.toggle('is-selected', selected);
        choice.classList.toggle('is-priority', priority);
        if (priority) priorityCount += 1;
      });

      if (selectedLabel && selectedInput) {
        selectedLabel.textContent = selectedInput.value;
      }

      if (countText) {
        const shown = expanded ? total : Math.min(total, initial + priorityCount);
        countText.textContent = `${shown} of ${total} shown`;
      }

      if (toggle) {
        const label = toggle.querySelector('[data-reign-style-toggle-label]');
        const template = expanded ? toggle.dataset.collapseLabel : toggle.dataset.expandLabel;
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        if (label) label.textContent = formatStylePickerLabel(template, total);
      }
    };

    stylePickers.forEach((picker) => {
      const toggle = picker.querySelector('[data-reign-style-toggle]');
      if (toggle) {
        toggle.addEventListener('click', () => {
          picker.classList.toggle('is-expanded');
          syncStylePicker(picker);
        });
      }
      syncStylePicker(picker);
    });

    const findVariantByOptions = (selections, requireAvailable = false) =>
      variants.find((variant) => {
        if (requireAvailable && !variant.available) return false;
        return variant.options.every((option, index) => option === selections[index]);
      });

    const setActiveMedia = (mediaId, options = {}) => {
      if (!mediaId) return;
      const { skipScroll } = options;
      mediaItems.forEach((item) => {
        item.classList.toggle('is-active', item.dataset.mediaId === String(mediaId));
      });
      mediaThumbs.forEach((thumb) => {
        const selected = thumb.dataset.mediaId === String(mediaId);
        thumb.classList.toggle('is-active', selected);
        thumb.setAttribute('aria-pressed', selected ? 'true' : 'false');
        if (selected && section.classList.contains('tl-september-product')) {
          const strip = thumb.parentElement;
          strip.scrollLeft = thumb.offsetLeft - strip.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2;
        }
      });
      modalItems.forEach((item) => {
        item.classList.toggle('is-active', item.dataset.mediaId === String(mediaId));
      });
      if (!skipScroll && mediaTrack && mediaTrack.scrollWidth > mediaTrack.clientWidth) {
        const activeItem = Array.from(mediaItems).find(
          (item) => item.dataset.mediaId === String(mediaId)
        );
        if (activeItem && typeof activeItem.scrollIntoView === 'function') {
          activeItem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }
    };

    const stepMedia = (direction) => {
      if (mediaOrder.length < 2) return;
      const active = section.querySelector('.reign-product__media-item.is-active');
      const activeId = active ? active.dataset.mediaId : mediaOrder[0];
      const currentIndex = mediaOrder.indexOf(activeId);
      const nextIndex = (currentIndex + direction + mediaOrder.length) % mediaOrder.length;
      const nextId = mediaOrder[nextIndex];
      if (nextId) {
        setActiveMedia(nextId);
      }
    };

    const getNearestMediaId = () => {
      if (!mediaTrack || !mediaItems.length) return null;
      const target = mediaTrack.scrollLeft;
      let nearestId = null;
      let nearestDistance = Infinity;
      mediaItems.forEach((item) => {
        const distance = Math.abs(item.offsetLeft - target);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestId = item.dataset.mediaId || null;
        }
      });
      return nearestId;
    };

    let mediaScrollTimer = null;
    const syncActiveFromScroll = () => {
      const nearestId = getNearestMediaId();
      if (nearestId) {
        setActiveMedia(nearestId, { skipScroll: true });
      }
    };

    const updateBadge = (variant) => {
      if (!priceWrap) return;
      let badge = priceWrap.querySelector('.reign-price__badge');
      if (priceWrap.querySelector('.reign-price__coupon')) {
        if (badge) {
          badge.remove();
        }
        return;
      }
      if (variant.compare_at_price > variant.price) {
        const savings = formatMoney(variant.compare_at_price - variant.price, moneyFormat);
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'reign-price__badge';
          priceWrap.appendChild(badge);
        }
        badge.textContent = `Save ${savings}`;
      } else if (badge) {
        badge.remove();
      }
    };

    const updateInventory = (variant) => {
      if (!inventoryEl || !inventoryText || !variant) return;
      const threshold = Number(inventoryEl.dataset.threshold || 0);
      const inStockText = inventoryEl.dataset.inStock || 'In stock';
      const lowStockText = inventoryEl.dataset.lowStock || 'Only [count] left';
      const outStockText = inventoryEl.dataset.outOfStock || 'Out of stock';
      const showCount = inventoryEl.dataset.showCount === 'true';

      inventoryEl.classList.remove('is-low', 'is-out');

      if (!variant.available) {
        inventoryText.textContent = outStockText;
        inventoryEl.classList.add('is-out');
        return;
      }

      const qty = typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : null;
      const formatText = (text) => {
        if (qty === null || Number.isNaN(qty)) return text.replace(/\[count\]/g, '');
        return showCount ? text.replace(/\[count\]/g, qty) : text.replace(/\[count\]/g, '');
      };

      if (variant.inventory_management && qty !== null && threshold > 0 && qty > 0 && qty <= threshold) {
        inventoryText.textContent = formatText(lowStockText);
        inventoryEl.classList.add('is-low');
      } else {
        inventoryText.textContent = formatText(inStockText);
      }
    };

    const updateQtyBreakPrices = (variant) => {
      if (!variant || !qtyBreaks.length) return;
      const unitPrice = typeof variant.price === 'number' ? variant.price : 0;
      const compareUnit =
        typeof variant.compare_at_price === 'number' && variant.compare_at_price > unitPrice
          ? variant.compare_at_price
          : unitPrice;

      qtyBreaks.forEach((input) => {
        const wrap = input.closest('.reign-qty-break');
        if (!wrap) return;
        const qty = Number(input.dataset.qty || input.value || 1);
        if (!qty || Number.isNaN(qty)) return;
        const discount = Number(input.dataset.discount || 0);
        const total = unitPrice * qty;
        const discounted = Math.round((total * (100 - discount)) / 100);
        const compareTotal = compareUnit * qty;

        const priceEl = wrap.querySelector('[data-reign-qty-price]');
        const compareEl = wrap.querySelector('[data-reign-qty-compare]');
        if (priceEl) {
          priceEl.textContent = formatMoney(discounted, moneyFormat);
        }
        if (compareEl) {
          if (compareTotal > discounted) {
            compareEl.textContent = formatMoney(compareTotal, moneyFormat);
            compareEl.hidden = false;
            compareEl.classList.remove('is-hidden');
          } else {
            compareEl.hidden = true;
            compareEl.classList.add('is-hidden');
          }
        }
      });
    };

    const updateQtyBreakHighlights = () => {
      qtyBreaks.forEach((input) => {
        const wrap = input.closest('.reign-qty-break');
        if (wrap) {
          wrap.classList.toggle('is-selected', input.checked);
        }
      });
    };

    const getQtyValue = () => {
      if (!qtyInput) return 1;
      const raw = Number(qtyInput.value || 1);
      return !Number.isNaN(raw) && raw > 0 ? raw : 1;
    };

    const findMatchingQtyBreak = (qty) =>
      Array.from(qtyBreaks).find((input) => {
        if (!input.checked) return false;
        const breakQty = Number(input.dataset.qty || input.value || 1);
        return !Number.isNaN(breakQty) && breakQty === qty;
      });

    const updateAtcPrice = (variant) => {
      if (!variant) return;
      const priceEl = addButton?.querySelector('[data-reign-atc-price]');
      const qty = getQtyValue();
      let total = (typeof variant.price === 'number' ? variant.price : 0) * qty;
      const selectedBreak = findMatchingQtyBreak(qty);
      if (selectedBreak) {
        const discount = Number(selectedBreak.dataset.discount || 0);
        if (!Number.isNaN(discount) && discount > 0) {
          total = Math.round((total * (100 - discount)) / 100);
        }
      }
      const formattedTotal = formatMoney(total, moneyFormat);
      if (priceEl) {
        priceEl.textContent = formattedTotal;
        priceEl.hidden = !variant.available || productForm?.dataset.reignSubmitting === 'true';
      }
      if (stickyPrice) {
        stickyPrice.textContent = formattedTotal;
        stickyPrice.hidden = !variant.available;
      }
      const selection = section.querySelector('[data-tl-purchase-selection]');
      const count = section.querySelector('[data-tl-purchase-count]');
      if (selection) selection.textContent = variant.title;
      if (count) {
        const adhesive = section.querySelector('.tl-set-inclusions') ? ' / Free adhesive included' : '';
        count.textContent = `${qty} ${qty === 1 ? 'set' : 'sets'}${adhesive}`;
      }
    };

    const syncBreaksToQty = () => {
      if (!qtyInput || !qtyBreaks.length) return;
      const qty = getQtyValue();
      let matched = false;
      qtyBreaks.forEach((input) => {
        const breakQty = Number(input.dataset.qty || input.value || 1);
        const shouldCheck = !Number.isNaN(breakQty) && breakQty === qty;
        if (shouldCheck && !matched) {
          input.checked = true;
          matched = true;
        } else if (input.checked && !shouldCheck) {
          input.checked = false;
        }
      });
      updateQtyBreakHighlights();
    };

    const updateUI = (variant) => {
      if (!variant) return;
      activeVariant = variant;
      const purchasePending = productForm?.dataset.reignSubmitting === 'true';
      if (variantInput) {
        variantInput.value = variant.id;
      }
      if (installmentInput) {
        installmentInput.value = variant.id;
      }
      if (priceCurrent) {
        priceCurrent.textContent = formatMoney(variant.price, moneyFormat);
      }
      if (priceCompare) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceCompare.textContent = formatMoney(variant.compare_at_price, moneyFormat);
          priceCompare.classList.remove('is-hidden');
        } else {
          priceCompare.classList.add('is-hidden');
        }
      }
      if (skuEl) {
        skuEl.textContent = variant.sku || '';
      }
      if (addButton && !purchasePending) {
        const addText = addButton.dataset.addText || 'Add to cart';
        const soldText = addButton.dataset.soldOutText || 'Sold out';
        const label = addButton.querySelector('[data-reign-atc-label]');
        const buttonText = variant.available ? addText : soldText;
        if (label) {
          label.textContent = buttonText;
        } else {
          addButton.textContent = buttonText;
        }
        addButton.disabled = !variant.available;
      }
      if (stickyVariant) {
        stickyVariant.textContent = variant.title;
      }
      if (stickyButton && !purchasePending) {
        const addText = stickyButton.dataset.addText || 'Add to cart';
        const soldText = stickyButton.dataset.soldOutText || 'Sold out';
        const stickyLabel = stickyButton.querySelector('[data-reign-atc-label]');
        if (stickyLabel) stickyLabel.textContent = variant.available ? addText : soldText;
        else stickyButton.textContent = variant.available ? addText : soldText;
        stickyButton.disabled = !variant.available;
      }
      updateBadge(variant);
      updateInventory(variant);
      updateQtyBreakPrices(variant);
      updateAtcPrice(variant);
      if (variant.featured_media && variant.featured_media.id) {
        setActiveMedia(variant.featured_media.id);
        const stickyImage = stickyBar?.querySelector('.reign-sticky-atc__image');
        const selectedImage = Array.from(mediaItems)
          .find((item) => item.dataset.mediaId === String(variant.featured_media.id))
          ?.querySelector('img');
        if (stickyImage && selectedImage) {
          stickyImage.removeAttribute('srcset');
          stickyImage.src = selectedImage.currentSrc || selectedImage.src;
          stickyImage.alt = variant.title;
        }
      }

      if (window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    };

    const setSelectedOptions = (variant) => {
      if (!variant) return;
      optionInputs.forEach((input) => {
        const index = Number(input.dataset.optionIndex);
        if (variant.options[index] === input.value) {
          input.checked = true;
        }
      });
    };

    const updateOptionAvailability = (selections) => {
      optionInputs.forEach((input) => {
        const index = Number(input.dataset.optionIndex);
        const value = input.value;
        const isAvailable = variants.some((variant) => {
          if (!variant.available) return false;
          if (variant.options[index] !== value) return false;
          return selections.every((selection, selectionIndex) => {
            if (selectionIndex === index) return true;
            if (!selection) return true;
            return variant.options[selectionIndex] === selection;
          });
        });
        input.disabled = !isAvailable;
        input.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
        const label = input.closest('.reign-variant__value');
        if (label) {
          label.classList.toggle('is-disabled', !isAvailable);
        }
      });
    };

    const findFallbackVariant = (selections) => {
      const match = variants.find((variant) => {
        if (!variant.available) return false;
        return selections.every((selection, index) => {
          if (!selection) return true;
          return variant.options[index] === selection;
        });
      });
      if (match) return match;
      return variants.find((variant) => variant.available) || variants[0];
    };

    const refreshVariant = () => {
      const selections = getSelectedOptions();
      updateOptionAvailability(selections);

      let variant = findVariantByOptions(selections, true);
      if (!variant) {
        variant = findVariantByOptions(selections);
      }

      if (!variant || !variant.available) {
        const fallback = findFallbackVariant(selections);
        if (fallback) {
          setSelectedOptions(fallback);
          variant = fallback;
        }
      }

      updateOptionAvailability(getSelectedOptions());
      updateUI(variant);
      if (duoOffer) duoOffer.sync(variant);
      stylePickers.forEach(syncStylePicker);
    };

    optionInputs.forEach((input) => {
      input.addEventListener('change', () => {
        refreshVariant();
      });
    });

    mediaThumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        setActiveMedia(thumb.dataset.mediaId);
      });
    });

    if (mediaPrev) {
      mediaPrev.addEventListener('click', () => stepMedia(-1));
    }

    if (mediaNext) {
      mediaNext.addEventListener('click', () => stepMedia(1));
    }

    if (mediaTrack) {
      mediaTrack.addEventListener(
        'scroll',
        () => {
          if (mediaScrollTimer) {
            clearTimeout(mediaScrollTimer);
          }
          mediaScrollTimer = window.setTimeout(syncActiveFromScroll, 120);
        },
        { passive: true }
      );
    }

    qtyButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (!qtyInput) return;
        const step = Number(button.dataset.reignQtyStep || 0);
        const current = Number(qtyInput.value || 1);
        const next = Math.max(1, current + step);
        qtyInput.value = next;
        syncBreaksToQty();
        updateAtcPrice(activeVariant);
      });
    });

    if (qtyInput) {
      const handleQtyInput = () => {
        syncBreaksToQty();
        updateAtcPrice(activeVariant);
      };
      qtyInput.addEventListener('change', handleQtyInput);
      qtyInput.addEventListener('input', handleQtyInput);
    }

    const syncQtyFromBreaks = () => {
      if (!qtyInput) return;
      const selected = Array.from(qtyBreaks).find((input) => input.checked);
      if (!selected) return;
      const qty = Number(selected.dataset.qty || selected.value || 1);
      if (!Number.isNaN(qty) && qty > 0) {
        qtyInput.value = qty;
      }
    };

    qtyBreaks.forEach((input) => {
      input.addEventListener('change', () => {
        if (!qtyInput) return;
        const qty = Number(input.dataset.qty || input.value || 1);
        if (!Number.isNaN(qty) && qty > 0) {
          qtyInput.value = qty;
        }
        updateQtyBreakHighlights();
        updateAtcPrice(activeVariant);
      });
    });

    updateQtyBreakHighlights();
    syncQtyFromBreaks();

    if (stickyBar && stickyForm && stickyButton) {
      stickyButton.addEventListener('click', () => {
        if (stickyButton.disabled) return;
        if (stickyForm.dataset.reignSubmitting === 'true') return;
        if (typeof stickyForm.requestSubmit === 'function') {
          stickyForm.requestSubmit();
        } else {
          stickyForm.submit();
        }
      });

      const footer = typeof document.querySelector === 'function' ? document.querySelector('.reign-footer, footer') : null;
      let stickyFrame = null;
      const toggleSticky = () => {
        stickyFrame = null;
        const formRect = typeof stickyForm.getBoundingClientRect === 'function' ? stickyForm.getBoundingClientRect() : { bottom: Number.POSITIVE_INFINITY };
        const footerRect = typeof footer?.getBoundingClientRect === 'function' ? footer.getBoundingClientRect() : null;
        const headerOffset = typeof getComputedStyle === 'function' && document.documentElement
          ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ten-love-sticky-offset')) || 0
          : 0;
        const purchaseHasPassed = formRect.bottom <= headerOffset;
        const purchaseButtonRect = addButton?.getBoundingClientRect?.();
        const productTitleRect = section.querySelector('.reign-product__title')?.getBoundingClientRect?.();
        const mobilePickerIsVisible = section.classList.contains('tl-september-product') && window.innerWidth < 750
          && purchaseButtonRect?.top >= window.innerHeight && productTitleRect?.top < window.innerHeight * 0.75;
        const footerIsVisible = footerRect && footerRect.top < window.innerHeight && footerRect.bottom > 0;
        const overlayIsOpen = typeof document.querySelector === 'function'
          ? Boolean(document.querySelector('dialog[open], [data-reign-modal][aria-hidden="false"], [data-reign-drawer].is-active'))
          : false;
        stickyBar.classList.toggle('is-visible', (purchaseHasPassed || mobilePickerIsVisible) && !footerIsVisible && !overlayIsOpen);
      };
      const scheduleStickyCheck = () => {
        if (stickyFrame) return;
        stickyFrame = typeof window.requestAnimationFrame === 'function' ? window.requestAnimationFrame(toggleSticky) : window.setTimeout(toggleSticky, 16);
      };
      if (typeof window.addEventListener === 'function') {
        window.addEventListener('scroll', scheduleStickyCheck, { passive: true });
        window.addEventListener('resize', scheduleStickyCheck);
      }
      if (typeof document.addEventListener === 'function') {
        document.addEventListener('toggle', scheduleStickyCheck, true);
        document.addEventListener('reign:drawer:change', scheduleStickyCheck);
      }
      toggleSticky();
    }

    section.reignSyncPurchaseState = () => {
      const selectedId = variantInput ? String(variantInput.value) : '';
      const selectedVariant = variants.find((variant) => String(variant.id) === selectedId);
      updateUI(selectedVariant || activeVariant);
      if (duoOffer) duoOffer.render();
    };

    refreshVariant();
    if (window.TenLoveDuo) {
      duoOffer = window.TenLoveDuo.init(section, variants, (cents) => formatMoney(cents, moneyFormat), (variant) => {
        setSelectedOptions(variant);
        refreshVariant();
      });
    }
  };

  const sliders = document.querySelectorAll('[data-reign-slider]');
  const parseBool = (value) => value === 'true' || value === true;
  const parseNumber = (value, fallback) => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : fallback;
  };
  sliders.forEach((slider) => {
    const track = slider.querySelector('[data-reign-slider-track]');
    if (!track) return;
    const prevButton = slider.querySelector('[data-reign-slider-prev]');
    const nextButton = slider.querySelector('[data-reign-slider-next]');
    const autoplay = parseBool(slider.dataset.reignSliderAutoplay);
    const pauseOnHover = parseBool(slider.dataset.reignSliderPause);
    const interval = parseNumber(slider.dataset.reignSliderInterval, 5000);
    let autoplayTimer = null;

    const scrollByAmount = (direction) => {
      const amount = track.clientWidth * 0.9;
      track.scrollBy({ left: amount * direction, behavior: 'smooth' });
    };

    if (prevButton) {
      prevButton.addEventListener('click', () => {
        scrollByAmount(-1);
        if (autoplay) startAutoplay();
      });
    }
    if (nextButton) {
      nextButton.addEventListener('click', () => {
        scrollByAmount(1);
        if (autoplay) startAutoplay();
      });
    }

    const stopAutoplay = () => {
      if (!autoplayTimer) return;
      window.clearInterval(autoplayTimer);
      autoplayTimer = null;
    };

    const startAutoplay = () => {
      if (!autoplay || interval < 1000) return;
      stopAutoplay();
      autoplayTimer = window.setInterval(() => {
        scrollByAmount(1);
      }, interval);
    };

    if (pauseOnHover) {
      slider.addEventListener('mouseenter', stopAutoplay);
      slider.addEventListener('mouseleave', startAutoplay);
      slider.addEventListener('focusin', stopAutoplay);
      slider.addEventListener('focusout', startAutoplay);
    }

    startAutoplay();
  });

  const initReveal = (scope = document) => {
    const elements = [];
    if (scope && scope.matches && scope.matches('[data-reign-reveal]')) {
      elements.push(scope);
    }
    if (scope && scope.querySelectorAll) {
      elements.push(...scope.querySelectorAll('[data-reign-reveal]'));
    }
    if (!elements.length) return;

    if (!('IntersectionObserver' in window)) {
      elements.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.2 }
    );

    elements.forEach((el) => {
      if (el.dataset.reignRevealInit === 'true') return;
      el.dataset.reignRevealInit = 'true';
      observer.observe(el);
    });
  };

  const scheduleRevealFallback = (scope = document) => {
    const elements = [];
    if (scope && scope.matches && scope.matches('[data-reign-reveal]')) {
      elements.push(scope);
    }
    if (scope && scope.querySelectorAll) {
      elements.push(...scope.querySelectorAll('[data-reign-reveal]'));
    }
    if (!elements.length) return;

    window.setTimeout(() => {
      elements.forEach((el) => {
        if (!el.classList.contains('is-visible')) {
          el.classList.add('is-visible');
        }
      });
    }, 1800);
  };

  const initRevealGroups = (scope = document) => {
    const groups = [];
    if (scope && scope.matches && scope.matches('[data-reign-reveal-group]')) {
      groups.push(scope);
    }
    if (scope && scope.querySelectorAll) {
      groups.push(...scope.querySelectorAll('[data-reign-reveal-group]'));
    }
    if (!groups.length) return;

    groups.forEach((group) => {
      Array.from(group.children).forEach((child) => {
        if (['SCRIPT', 'STYLE', 'TEMPLATE'].includes(child.tagName)) return;
        if (!child.classList.contains('reign-reveal')) {
          child.classList.add('reign-reveal');
        }
        if (!child.hasAttribute('data-reign-reveal')) {
          child.setAttribute('data-reign-reveal', '');
        }
      });
    });
  };

  const initSplitText = (scope = document) => {
    const elements = new Set();

    if (scope && scope.matches && scope.matches('[data-reign-split]')) {
      elements.add(scope);
    }
    if (scope && scope.querySelectorAll) {
      scope.querySelectorAll('[data-reign-split]').forEach((el) => elements.add(el));
    }

    const allowAutoSplit =
      document.body &&
      (document.body.classList.contains('reign-page-index') ||
        document.body.classList.contains('reign-page-product'));

    if (scope && scope.querySelectorAll) {
      const selector = allowAutoSplit ? '.reign-hero__heading, .reign-section__title' : '.reign-hero__heading';
      scope.querySelectorAll(selector).forEach((el) => elements.add(el));
    }

    if (!elements.size) return;

    const splitTargets = [];
    elements.forEach((el) => {
      if (el.dataset.reignSplitInit === 'true') return;
      if (el.children.length > 0) return;
      const text = el.textContent;
      if (!text || !text.trim()) return;

      const words = text.trim().split(/\s+/);
      if (!words.length) return;

      el.textContent = '';
      words.forEach((word, index) => {
        const wordSpan = document.createElement('span');
        wordSpan.className = 'reign-split__word';
        wordSpan.style.setProperty('--reign-split-delay', `${index * 70}ms`);
        wordSpan.textContent = word;
        el.appendChild(wordSpan);
        if (index < words.length - 1) {
          el.appendChild(document.createTextNode(' '));
        }
      });

      el.classList.add('reign-split');
      el.dataset.reignSplitInit = 'true';
      splitTargets.push(el);
    });

    if (!splitTargets.length) return;

    const prefersReducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      splitTargets.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -15% 0px', threshold: 0.2 }
    );

    splitTargets.forEach((el) => {
      if (el.dataset.reignSplitObserver === 'true') return;
      el.dataset.reignSplitObserver = 'true';
      observer.observe(el);
    });
  };

  const parseCountValue = (text) => {
    if (!text) return null;
    const match = text.match(/-?\d[\d,.]*/);
    if (!match) return null;
    const rawNumber = match[0];
    const number = Number(rawNumber.replace(/,/g, ''));
    if (Number.isNaN(number)) return null;
    const decimals = rawNumber.includes('.') ? rawNumber.split('.')[1].length : 0;
    return {
      number,
      decimals,
      prefix: text.slice(0, match.index),
      suffix: text.slice(match.index + rawNumber.length),
    };
  };

  const initCountUps = (scope = document) => {
    const elements = [];
    if (scope && scope.matches && scope.matches('[data-reign-countup]')) {
      elements.push(scope);
    }
    if (scope && scope.querySelectorAll) {
      elements.push(...scope.querySelectorAll('[data-reign-countup]'));
    }
    if (!elements.length) return;

    const animateCount = (el) => {
      if (el.dataset.reignCountInit === 'true') return;
      const parsed = parseCountValue(el.textContent || '');
      if (!parsed) return;
      el.dataset.reignCountInit = 'true';
      const { number, decimals, prefix, suffix } = parsed;
      const duration = 1200;
      const start = performance.now();

      const formatNumber = (value) => {
        if (decimals > 0) {
          return value.toLocaleString(undefined, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          });
        }
        return Math.round(value).toLocaleString();
      };

      const step = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const current = number * progress;
        el.textContent = `${prefix}${formatNumber(current)}${suffix}`;
        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };

      requestAnimationFrame(step);
    };

    if (!('IntersectionObserver' in window)) {
      elements.forEach(animateCount);
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          animateCount(entry.target);
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -15% 0px', threshold: 0.3 }
    );

    elements.forEach((el) => {
      if (el.dataset.reignCountObserver === 'true') return;
      el.dataset.reignCountObserver = 'true';
      observer.observe(el);
    });
  };

  const initAnimations = (scope = document) => {
    initRevealGroups(scope);
    initReveal(scope);
    scheduleRevealFallback(scope);
    initSplitText(scope);
    initCountUps(scope);
  };

  const initFeaturedProductGallery = (scope = document) => {
    const galleries = scope.querySelectorAll('[data-reign-featured-gallery]');
    if (!galleries.length) return;

    galleries.forEach((gallery) => {
      if (gallery.dataset.reignFeaturedInit === 'true') return;
      const mainImage = gallery.querySelector('[data-reign-featured-main]');
      const thumbs = Array.from(gallery.querySelectorAll('[data-reign-featured-thumb]'));
      if (!mainImage || !thumbs.length) return;
      gallery.dataset.reignFeaturedInit = 'true';

      const setActive = (thumb) => {
        if (!thumb) return;
        const src = thumb.dataset.reignSrc;
        if (src) mainImage.src = src;
        const srcset = thumb.dataset.reignSrcset;
        if (srcset) mainImage.srcset = srcset;
        const alt = thumb.dataset.reignAlt;
        if (alt) mainImage.alt = alt;
        thumbs.forEach((item) => {
          const isActive = item === thumb;
          item.classList.toggle('is-active', isActive);
          item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      };

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => setActive(thumb));
      });
    });
  };

  const initPressCarousels = (scope = document) => {
    const sections = [];
    if (scope && scope.matches && scope.matches('[data-reign-press]')) {
      sections.push(scope);
    }
    if (scope && scope.querySelectorAll) {
      sections.push(...scope.querySelectorAll('[data-reign-press]'));
    }

    sections.forEach((section) => {
      if (section.dataset.reignPressInit === 'true') return;
      section.dataset.reignPressInit = 'true';
      const logos = Array.from(section.querySelectorAll('[data-press-logo]'));
      const quotes = Array.from(section.querySelectorAll('[data-press-quote]'));
      const prevButton = section.querySelector('[data-press-prev]');
      const nextButton = section.querySelector('[data-press-next]');
      const intervalMs = Number(section.dataset.pressInterval || 0) * 1000;
      if (!logos.length || !quotes.length) return;

      let index = logos.findIndex((logo) => logo.classList.contains('is-active'));
      if (index < 0) index = 0;
      let timer;

      const activate = (nextIndex) => {
        const total = logos.length;
        const safeIndex = ((nextIndex % total) + total) % total;
        index = safeIndex;
        logos.forEach((logo, logoIndex) => {
          const isActive = logoIndex === safeIndex;
          logo.classList.toggle('is-active', isActive);
          logo.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        quotes.forEach((quote) => {
          const quoteIndex = Number(quote.dataset.pressIndex || 0);
          quote.classList.toggle('is-active', quoteIndex === safeIndex);
        });
      };

      const stopTimer = () => {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      };

      const startTimer = () => {
        if (!intervalMs) return;
        stopTimer();
        timer = setInterval(() => {
          activate(index + 1);
        }, intervalMs);
      };

      logos.forEach((logo) => {
        logo.addEventListener('click', () => {
          const nextIndex = Number(logo.dataset.pressIndex || 0);
          activate(nextIndex);
          startTimer();
        });
      });

      if (prevButton) {
        prevButton.addEventListener('click', () => {
          activate(index - 1);
          startTimer();
        });
      }

      if (nextButton) {
        nextButton.addEventListener('click', () => {
          activate(index + 1);
          startTimer();
        });
      }

      section.addEventListener('mouseenter', stopTimer);
      section.addEventListener('mouseleave', startTimer);

      activate(index);
      startTimer();
    });
  };

  const tabSections = document.querySelectorAll('[data-reign-tabs]');
  tabSections.forEach((section) => {
    const tabs = section.querySelectorAll('[data-reign-tab]');
    const panels = section.querySelectorAll('[data-reign-tab-panel]');

    const activateTab = (id) => {
      tabs.forEach((tab) => {
        const isActive = tab.dataset.reignTab === id;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      panels.forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.reignTabPanel === id);
      });
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => activateTab(tab.dataset.reignTab));
    });
  });

  const compareSections = document.querySelectorAll('[data-reign-compare]');
  compareSections.forEach((section) => {
    const range = section.querySelector('[data-reign-compare-range]');
    if (!range) return;
    const frame = section.querySelector('.reign-compare__frame');
    const update = (value) => {
      const next = Math.min(100, Math.max(0, Number(value)));
      range.value = next;
      section.style.setProperty('--compare-position', `${next}%`);
    };
    const updateFromRange = () => update(range.value);
    range.addEventListener('input', updateFromRange);
    updateFromRange();

    if (!frame) return;
    const updateFromEvent = (event) => {
      const rect = frame.getBoundingClientRect();
      const clientX =
        event.clientX ??
        (event.touches && event.touches[0] && event.touches[0].clientX) ??
        (event.changedTouches && event.changedTouches[0] && event.changedTouches[0].clientX);
      if (typeof clientX !== 'number' || rect.width === 0) return;
      const percent = ((clientX - rect.left) / rect.width) * 100;
      update(percent);
    };

    let isDragging = false;
    let activePointerId = null;
    const startDrag = (event) => {
      isDragging = true;
      if (event && typeof event.pointerId === 'number') {
        activePointerId = event.pointerId;
        if (frame.setPointerCapture) {
          try {
            frame.setPointerCapture(event.pointerId);
          } catch (_) {
            activePointerId = null;
          }
        }
      }
      updateFromEvent(event);
    };
    const stopDrag = () => {
      if (
        activePointerId !== null &&
        frame.releasePointerCapture &&
        frame.hasPointerCapture &&
        frame.hasPointerCapture(activePointerId)
      ) {
        try {
          frame.releasePointerCapture(activePointerId);
        } catch (_) {
          // no-op
        }
      }
      isDragging = false;
      activePointerId = null;
    };

    if (window.PointerEvent) {
      frame.addEventListener('pointerdown', startDrag);
      frame.addEventListener('click', updateFromEvent);
      frame.addEventListener('pointermove', (event) => {
        if (!isDragging) return;
        updateFromEvent(event);
      });
      frame.addEventListener('pointerup', stopDrag);
      frame.addEventListener('pointercancel', stopDrag);
      frame.addEventListener('lostpointercapture', stopDrag);
      frame.addEventListener('pointerleave', (event) => {
        if (!isDragging || (event.buttons && event.buttons > 0)) return;
        stopDrag();
      });
      window.addEventListener('pointerup', stopDrag);
      window.addEventListener('pointercancel', stopDrag);
    } else {
      frame.addEventListener('mousedown', (event) => {
        startDrag(event);
      });
      window.addEventListener('mousemove', (event) => {
        if (!isDragging) return;
        updateFromEvent(event);
      });
      window.addEventListener('mouseup', stopDrag);
      frame.addEventListener(
        'touchstart',
        (event) => {
          startDrag(event);
          if (event.cancelable) event.preventDefault();
        },
        { passive: false }
      );
      window.addEventListener(
        'touchmove',
        (event) => {
          if (!isDragging) return;
          updateFromEvent(event);
          if (event.cancelable) event.preventDefault();
        },
        { passive: false }
      );
      window.addEventListener('touchend', stopDrag);
      window.addEventListener('touchcancel', stopDrag);
    }
  });

  const popups = document.querySelectorAll('[data-reign-popup]');
  popups.forEach((popup) => {
    const delay = Number(popup.dataset.popupDelay || 0);
    const key = popup.dataset.popupKey || 'reign-popup';
    const closeButtons = popup.querySelectorAll('[data-reign-popup-close]');

    if (window.localStorage && window.localStorage.getItem(key)) {
      return;
    }

    window.setTimeout(() => {
      popup.classList.add('is-active');
    }, delay * 1000);

    closeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        popup.classList.remove('is-active');
        if (window.localStorage) {
          window.localStorage.setItem(key, 'closed');
        }
      });
    });
  });

  const scrollButtons = document.querySelectorAll('[data-reign-scroll-top]');
  if (scrollButtons.length) {
    const toggleButtons = () => {
      const show = window.scrollY > 400;
      scrollButtons.forEach((button) => {
        button.classList.toggle('is-visible', show);
      });
    };
    scrollButtons.forEach((button) => {
      button.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    });
    window.addEventListener('scroll', toggleButtons);
    toggleButtons();
  }

  const paletteStorageKey = 'reign_palette_override_v1';
  const inThemeEditor = Boolean(window.Shopify && window.Shopify.designMode);
  let activePalette = null;
  const buildAccentGradient = (accent) => `linear-gradient(135deg, ${accent} 0%, ${accent} 100%)`;
  const applyPaletteToScope = (palette, scope = document) => {
    if (!palette || typeof palette !== 'object') return;
    const { bg, fg, accent } = palette;
    const rootStyle = document.documentElement.style;
    if (bg) rootStyle.setProperty('--color-background', bg);
    if (fg) rootStyle.setProperty('--color-foreground', fg);
    if (accent) {
      rootStyle.setProperty('--color-accent', accent);
      rootStyle.setProperty('--color-accent-gradient', buildAccentGradient(accent));
    }
  };

  try {
    const savedPaletteRaw = window.localStorage ? window.localStorage.getItem(paletteStorageKey) : null;
    if (!inThemeEditor && savedPaletteRaw) {
      const savedPalette = JSON.parse(savedPaletteRaw);
      if (savedPalette && typeof savedPalette === 'object') {
        activePalette = savedPalette;
        applyPaletteToScope(activePalette, document);
      }
    }
  } catch (error) {
    activePalette = null;
  }

  document.addEventListener('click', (event) => {
    const button = event.target && event.target.closest ? event.target.closest('[data-reign-color]') : null;
    if (!button) return;
    event.preventDefault();
    const { bg, fg, accent } = button.dataset;
    activePalette = { bg: bg || '', fg: fg || '', accent: accent || '' };
    applyPaletteToScope(activePalette, document);
    document.querySelectorAll('[data-reign-color]').forEach((item) => {
      item.classList.toggle('is-active', item === button);
    });
    try {
      if (window.localStorage) {
        window.localStorage.setItem(paletteStorageKey, JSON.stringify(activePalette));
      }
    } catch (error) {
      return;
    }
  });

  const body = document.body;
  const cartUrl = body.dataset.reignCartUrl || '/cart';
  const cartAddUrl = body.dataset.reignCartAdd || '/cart/add';
  const cartChangeUrl = body.dataset.reignCartChange || '/cart/change';
  const cartUpdateUrl = body.dataset.reignCartUpdate || '/cart/update';
  const cartType = body.dataset.reignCartType || 'page';
  const cartDrawer = document.querySelector('[data-reign-cart-drawer]');
  const cartToast = document.querySelector('[data-reign-cart-toast]');
  const cartCountEls = document.querySelectorAll('[data-reign-cart-count]');

  const withJsonEndpoint = (url) => (url.endsWith('.js') ? url : `${url}.js`);
  const cartAddEndpoint = withJsonEndpoint(cartAddUrl);
  const cartChangeEndpoint = withJsonEndpoint(cartChangeUrl);
  const cartUpdateEndpoint = withJsonEndpoint(cartUpdateUrl);

  const updateCartCount = (count) => {
    cartCountEls.forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-hidden', count <= 0);
    });
  };

  const fetchCart = async (signal) => {
    try {
      const response = await fetch(withJsonEndpoint(cartUrl), { signal, cache: 'no-store' });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  };

  const showCartToast = () => {
    if (!cartToast) return;
    if (hideCartToast.timeoutId) {
      window.clearTimeout(hideCartToast.timeoutId);
    }
    cartToast.hidden = false;
    cartToast.classList.add('is-active');
    window.clearTimeout(showCartToast.timeoutId);
    showCartToast.timeoutId = window.setTimeout(hideCartToast, 4000);
  };

  const hideCartToast = () => {
    if (!cartToast) return;
    cartToast.classList.remove('is-active');
    hideCartToast.timeoutId = window.setTimeout(() => {
      cartToast.hidden = true;
    }, 220);
  };

  if (cartToast) {
    const closeButton = cartToast.querySelector('[data-reign-cart-toast-close]');
    if (closeButton) {
      closeButton.addEventListener('click', hideCartToast);
    }
  }

  const refreshCartDrawer = async (signal) => {
    if (!cartDrawer) return false;
    const content = cartDrawer.querySelector('[data-reign-cart-drawer-content]');
    if (!content) return false;
    const sectionId = content.dataset.reignSectionId || 'cart-drawer';
    try {
      const response = await fetch(`${cartUrl}?section_id=${sectionId}`, { signal });
      if (!response.ok) return false;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newContent = doc.querySelector('[data-reign-cart-drawer-content]');
      const newTitle = doc.querySelector('.reign-cart-drawer__title');
      const title = cartDrawer.querySelector('.reign-cart-drawer__title');
      if (newContent) {
        content.innerHTML = newContent.innerHTML;
        delete cartDrawer.dataset.reignCartEventsInit;
        bindCartDrawerEvents();
      } else {
        return false;
      }
      if (newTitle && title) {
        title.textContent = newTitle.textContent;
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  const refreshCartPage = async (signal) => {
    const section = document.querySelector('[data-reign-cart-section]');
    if (!section) return true;
    const sectionId = section.dataset.sectionId || section.getAttribute('data-section-id') || 'cart';
    try {
      const response = await fetch(`${cartUrl}?section_id=${sectionId}`, { signal });
      if (!response.ok) return false;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newSection = doc.querySelector('[data-reign-cart-section]');
      if (!newSection) return false;
      section.innerHTML = newSection.innerHTML;
      delete section.dataset.reignCartQtyInit;
      bindCartPageQuantity();
      bindDiscountButtons(section);
      bindShippingEstimate(section);
      bindAjaxAddForms(section);
      return true;
    } catch (error) {
      return false;
    }
  };

  let cartLineUpdating = false;
  let cartNeedsReload = false;
  const cartRegions = () => document.querySelectorAll('[data-reign-cart-section], [data-reign-cart-drawer]');
  const setCartLineBusy = (busy) => {
    cartRegions().forEach((region) => {
      region.setAttribute('aria-busy', String(busy));
      region.querySelectorAll('[data-reign-cart-qty], [data-reign-cart-input], button[name="checkout"], button[name="update"]').forEach((control) => {
        if (busy && !('reignCartWasDisabled' in control.dataset)) {
          control.dataset.reignCartWasDisabled = String(control.disabled);
        }
        control.disabled = busy || cartNeedsReload || control.dataset.reignCartWasDisabled === 'true';
        if (!busy) delete control.dataset.reignCartWasDisabled;
      });
      region.querySelectorAll('[data-reign-cart-remove]').forEach((link) => {
        link.setAttribute('aria-disabled', String(busy || cartNeedsReload));
      });
    });
  };
  const cartLineNotice = (message, reload = false) => {
    cartRegions().forEach((region) => {
      region.querySelector('[data-reign-cart-update-error]')?.remove();
      if (!message) return;
      const notice = document.createElement('p');
      notice.className = 'reign-cart-error';
      notice.setAttribute('data-reign-cart-update-error', '');
      notice.setAttribute('role', 'alert');
      notice.textContent = message;
      if (reload) {
        const link = document.createElement('a');
        link.href = cartUrl;
        link.textContent = 'Reload cart';
        notice.appendChild(document.createTextNode(' '));
        notice.appendChild(link);
      }
      (region.querySelector('form') || region).appendChild(notice);
    });
  };
  document.addEventListener('submit', (event) => {
    if ((cartLineUpdating || cartNeedsReload) && event.target.closest?.('[data-reign-cart-section], [data-reign-cart-drawer]')) {
      event.preventDefault();
    }
  }, true);

  const updateCartLine = async (line, quantity) => {
    if (!line || cartLineUpdating || cartNeedsReload) return;
    if (!Number.isSafeInteger(quantity) || quantity < 0) {
      cartRegions().forEach((region) => region.querySelectorAll('[data-reign-cart-input]').forEach((input) => {
        input.value = input.getAttribute('value');
      }));
      cartLineNotice('Please enter a whole-number quantity of zero or more.');
      return;
    }
    cartLineUpdating = true;
    cartLineNotice('');
    setCartLineBusy(true);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(cartChangeEndpoint, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        // Discounted items can split/reorder. Prefer Shopify's current line key.
        body: JSON.stringify({ ...(typeof line === 'string' ? { id: line } : { line }), quantity }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        const message = data?.description || data?.message;
        throw new Error(typeof message === 'string' ? message : 'This quantity could not be updated.');
      }
      if (!data || !Array.isArray(data.items)) throw new Error('The cart response could not be confirmed.');
      updateCartCount(data.item_count || 0);
      const drawerUpdated = cartDrawer ? await refreshCartDrawer(controller.signal) : true;
      const pageUpdated = await refreshCartPage(controller.signal);
      if (!drawerUpdated || !pageUpdated) throw new Error('Your cart changed, but its display could not refresh.');
    } catch (error) {
      cartNeedsReload = true;
      cartLineNotice(error.name === 'AbortError' || error instanceof TypeError
        ? 'We could not confirm the update. Reload your cart to check its contents before continuing.'
        : `${error.message} Reload your cart before continuing.`, true);
    } finally {
      clearTimeout(timeout);
      cartLineUpdating = false;
      setCartLineBusy(false);
    }
  };

  const queueCartLineUpdate = (line, quantity) => {
    // Lock synchronously so checkout or another line edit cannot race this write.
    return updateCartLine(line, quantity);
  };

  const refreshCartAfterHistory = async () => {
    cartLineUpdating = true;
    cartNeedsReload = false;
    cartLineNotice('');
    setCartLineBusy(true);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const cart = await fetchCart(controller.signal);
      if (!cart || !Array.isArray(cart.items)) throw new Error('Unconfirmed cart');
      updateCartCount(cart.item_count || 0);
      const drawerUpdated = cartDrawer ? await refreshCartDrawer(controller.signal) : true;
      const pageUpdated = await refreshCartPage(controller.signal);
      if (!drawerUpdated || !pageUpdated) throw new Error('Stale cart display');
    } catch (error) {
      cartNeedsReload = true;
      cartLineNotice('Your saved cart could not refresh. Reload it to see its current contents.', true);
    } finally {
      clearTimeout(timeout);
      cartLineUpdating = false;
      setCartLineBusy(false);
    }
  };
  window.addEventListener('pageshow', (event) => {
    // Checkout changes the cart while the browser can retain the old page in memory.
    if (event.persisted) return refreshCartAfterHistory();
  });

  const bindDiscountButtons = (scope = document) => {
    const buttons = scope.querySelectorAll('[data-reign-discount-apply]');
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const container = button.closest('[data-reign-discount]');
        if (!container) return;
        const input = container.querySelector('[data-reign-discount-input]');
        const code = input ? input.value.trim() : '';
        if (!code) return;
        const redirect = encodeURIComponent(cartUrl);
        window.location.href = `/discount/${encodeURIComponent(code)}?redirect=${redirect}`;
      });
    });
  };

  const bindShippingEstimate = (scope = document) => {
    const blocks = scope.querySelectorAll('[data-reign-shipping-estimate]');
    blocks.forEach((block) => {
      const zip = block.querySelector('[data-reign-shipping-zip]');
      const country = block.querySelector('[data-reign-shipping-country]');
      const results = block.querySelector('[data-reign-shipping-results]');
      const submit = block.querySelector('[data-reign-shipping-submit]');

      if (!zip || !country || !results || !submit) return;

      const defaultCountry = country.dataset.default;
      if (defaultCountry) {
        const option = Array.from(country.options).find((opt) => opt.value === defaultCountry);
        if (option) {
          country.value = defaultCountry;
        }
      }

      submit.addEventListener('click', async () => {
        const zipValue = zip.value.trim();
        const countryValue = country.value;
        if (!zipValue || !countryValue) {
          results.textContent = 'Enter a zip and country to see rates.';
          return;
        }

        const params = new URLSearchParams();
        params.append('shipping_address[zip]', zipValue);
        params.append('shipping_address[country]', countryValue);
        results.textContent = 'Calculating...';

        try {
          const response = await fetch(`${cartUrl}/shipping_rates.json?${params.toString()}`, {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) {
            results.textContent = 'No rates found.';
            return;
          }
          const data = await response.json();
          if (!data.shipping_rates || !data.shipping_rates.length) {
            results.textContent = 'No rates found.';
            return;
          }
          results.innerHTML = data.shipping_rates
            .map((rate) => `${rate.name}: ${formatMoney(rate.price, body.dataset.moneyFormat || '${{amount}}')}`)
            .join('<br>');
        } catch (error) {
          results.textContent = 'Unable to calculate shipping.';
        }
      });
    });
  };

  const bindShareButtons = (scope = document) => {
    const buttons = scope.querySelectorAll('[data-reign-share-copy]');
    buttons.forEach((button) => {
      if (button.dataset.reignShareBound) return;
      button.dataset.reignShareBound = 'true';
      button.addEventListener('click', async () => {
        const url = button.dataset.shareUrl || window.location.href;
        const original = button.textContent;
        const setLabel = (label) => {
          button.textContent = label;
          window.setTimeout(() => {
            button.textContent = original;
          }, 2000);
        };
        try {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(url);
            setLabel('Copied');
            return;
          }
          const temp = document.createElement('input');
          temp.value = url;
          document.body.appendChild(temp);
          temp.select();
          document.execCommand('copy');
          document.body.removeChild(temp);
          setLabel('Copied');
        } catch (error) {
          setLabel('Copy failed');
        }
      });
    });
  };

  const bindCopyButtons = (scope = document) => {
    const copyButtons = scope.querySelectorAll('[data-reign-copy-code]');
    copyButtons.forEach((button) => {
      if (button.dataset.reignCopyBound) return;
      button.dataset.reignCopyBound = 'true';
      button.addEventListener('click', async () => {
        const code = (button.dataset.code || '').trim();
        if (!code) return;
        const label = button.querySelector('[data-reign-copy-label]');
        const successText = button.dataset.successText || 'Copied!';
        const originalText = label ? label.textContent : '';

        const setLabel = (text) => {
          if (label) {
            label.textContent = text;
          }
          button.classList.add('is-copied');
          window.setTimeout(() => {
            if (label) {
              label.textContent = originalText;
            }
            button.classList.remove('is-copied');
          }, 2000);
        };

        try {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(code);
            setLabel(successText);
            return;
          }
          const temp = document.createElement('textarea');
          temp.value = code;
          temp.setAttribute('readonly', 'readonly');
          temp.style.position = 'absolute';
          temp.style.left = '-9999px';
          document.body.appendChild(temp);
          temp.select();
          document.execCommand('copy');
          document.body.removeChild(temp);
          setLabel(successText);
        } catch (error) {
          setLabel('Copy failed');
        }
      });
    });
  };

  const initAnnouncementBars = (scope = document) => {
    const bars = scope.querySelectorAll('[data-reign-announcement]');
    bars.forEach((bar) => {
      if (bar.dataset.reignAnnouncementInit === 'true') return;
      bar.dataset.reignAnnouncementInit = 'true';
      bindCopyButtons(bar);

      const rotate = bar.dataset.rotate === 'true';
      const items = Array.from(bar.querySelectorAll('[data-announcement-item]'));
      if (!rotate || items.length < 2) return;

      if (bar.reignRotateTimer) {
        window.clearInterval(bar.reignRotateTimer);
      }

      let activeIndex = 0;
      items.forEach((item, index) => {
        item.classList.toggle('is-active', index === activeIndex);
      });

      const interval = Number(bar.dataset.interval) || 4000;
      bar.reignRotateTimer = window.setInterval(() => {
        items[activeIndex].classList.remove('is-active');
        activeIndex = (activeIndex + 1) % items.length;
        items[activeIndex].classList.add('is-active');
      }, interval);
    });
  };

  const bindBundleDeals = (scope = document) => {
    const bundles = scope.querySelectorAll('[data-reign-bundle]');
    bundles.forEach((bundle) => {
      if (bundle.dataset.reignBundleInit === 'true') return;
      bundle.dataset.reignBundleInit = 'true';
      const items = bundle.querySelectorAll('[data-reign-bundle-item]');
      const submitButton = bundle.querySelector('[data-reign-bundle-submit]');
      const notice = bundle.querySelector('[data-reign-bundle-notice]');
      const form = bundle.querySelector('.reign-bundle__form');
      const totalEl = bundle.querySelector('[data-reign-bundle-total]');
      const compareEl = bundle.querySelector('[data-reign-bundle-compare]');
      const savingsEl = bundle.querySelector('[data-reign-bundle-savings]');
      const moneyFormat =
        body.dataset.moneyFormat ||
        bundle.closest('[data-money-format]')?.dataset.moneyFormat ||
        '${{amount}}';

      if (!items.length) return;

      const updateAll = () => {
        let selectedCount = 0;
        let selectionIndex = 0;
        let total = 0;
        let compareTotal = 0;
        items.forEach((item) => {
          const checkbox = item.querySelector('[data-reign-bundle-checkbox]');
          const idInput = item.querySelector('[data-reign-bundle-id]');
          const qtyInput = item.querySelector('[data-reign-bundle-qty]');
          const selected = checkbox ? checkbox.checked : false;

          item.classList.toggle('is-selected', selected);
          if (idInput) {
            idInput.disabled = !selected;
            if (selected) {
              idInput.name = `items[${selectionIndex}][id]`;
            } else {
              idInput.removeAttribute('name');
            }
          }
          if (qtyInput) {
            qtyInput.disabled = !selected;
            if (selected) {
              qtyInput.name = `items[${selectionIndex}][quantity]`;
            } else {
              qtyInput.removeAttribute('name');
            }
          }

          if (selected) {
            selectedCount += 1;
            selectionIndex += 1;
            const price = Number(item.dataset.price || 0);
            const compare = Number(item.dataset.compare || price);
            total += price;
            compareTotal += compare;
          }
        });
        if (submitButton) {
          submitButton.disabled = selectedCount === 0;
        }
        if (notice) {
          notice.hidden = selectedCount !== 0;
        }
        if (totalEl) {
          totalEl.textContent = formatMoney(total, moneyFormat);
        }
        if (compareEl) {
          if (compareTotal > total) {
            compareEl.textContent = formatMoney(compareTotal, moneyFormat);
            compareEl.hidden = false;
          } else {
            compareEl.textContent = '';
            compareEl.hidden = true;
          }
        }
        if (savingsEl) {
          if (compareTotal > total) {
            savingsEl.textContent = `Save ${formatMoney(compareTotal - total, moneyFormat)}`;
            savingsEl.hidden = false;
          } else {
            savingsEl.textContent = '';
            savingsEl.hidden = true;
          }
        }
      };

      items.forEach((item) => {
        const checkbox = item.querySelector('[data-reign-bundle-checkbox]');
        if (checkbox) {
          checkbox.addEventListener('change', updateAll);
        }
      });

      if (form) {
        form.addEventListener('submit', (event) => {
          const hasSelection = Array.from(items).some((item) => {
            const checkbox = item.querySelector('[data-reign-bundle-checkbox]');
            return checkbox && checkbox.checked;
          });
          if (!hasSelection) {
            event.preventDefault();
            event.stopImmediatePropagation();
            if (notice) notice.hidden = false;
          }
        });
      }

      updateAll();
    });
  };

  const initBundlePurchaseToggle = (scope = document) => {
    const toggles = scope.querySelectorAll('[data-reign-bundle-toggle]');
    toggles.forEach((toggle) => {
      if (toggle.dataset.reignBundleToggleInit === 'true') return;
      toggle.dataset.reignBundleToggleInit = 'true';

      const wrapper = toggle.closest('.bundle-offer');
      const content = wrapper ? wrapper.querySelector('[data-reign-bundle-content]') : null;
      const section = toggle.closest('[data-reign-product]');
      const purchase = section ? section.querySelector('.reign-product__purchase') : null;
      const stickyBar = section ? section.querySelector('[data-reign-sticky-atc]') : null;
      const bundleType = wrapper ? wrapper.dataset.bundleType : 'separate';
      const buttons = Array.from(toggle.querySelectorAll('[data-reign-bundle-mode]'));
      if (!buttons.length) return;

      const setMode = (mode) => {
        const nextMode = mode === 'single' ? 'single' : 'bundle';
        if (wrapper) wrapper.dataset.bundleMode = nextMode;
        buttons.forEach((button) => {
          const isActive = button.dataset.reignBundleMode === nextMode;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        if (content) {
          content.hidden = nextMode === 'single';
        }
        if (purchase) {
          if (bundleType === 'separate') {
            purchase.hidden = nextMode === 'bundle';
          } else {
            purchase.hidden = false;
          }
        }
        if (stickyBar) {
          if (bundleType === 'separate') {
            stickyBar.hidden = nextMode === 'bundle';
          } else {
            stickyBar.hidden = false;
          }
        }
      };

      const defaultMode = toggle.dataset.defaultMode || 'bundle';
      setMode(defaultMode);

      buttons.forEach((button) => {
        button.addEventListener('click', () => setMode(button.dataset.reignBundleMode));
      });
    });
  };

  const parseBundleMoney = (value) => Number(String(value || '').replace(/[^\d]/g, '')) || 0;

  const getBundleLabel = (wrapper, count) => {
    const solo = wrapper?.dataset.bundleLabelSolo || 'Solo Deal';
    const duo = wrapper?.dataset.bundleLabelDuo || 'Duo Deal';
    const trio = wrapper?.dataset.bundleLabelTrio || 'Trio Deal';
    const fallback = wrapper?.dataset.bundleLabelDefault || 'Bundle Deal';
    if (count === 1) return solo;
    if (count === 2) return duo;
    if (count >= 3) return trio;
    return fallback;
  };

  const updateBundleOfferTotals = (offer) => {
    if (!offer) return;
    const checkboxes = Array.from(offer.querySelectorAll('.bundle-deals__checkbox-js'));
    if (!checkboxes.length) return;

    const wrapper = offer.closest('.bundle-offer');
    const totalPriceEl = offer.querySelector('.bundle-deals__total-price-js');
    const totalCompareEl = offer.querySelector('.bundle-deals__total-compare-price-js');
    const moneyFormat = offer.dataset.moneyFormat || body.dataset.moneyFormat || '${{amount}}';
    const totalPercentageLeft = parseFloat(offer.dataset.percentageLeft || 1);
    const totalFixedDiscount = parseFloat(offer.dataset.fixedDiscount || 0);
    let total = 0;
    let compareTotal = 0;
    let selectedCount = 0;

    checkboxes.forEach((checkbox) => {
      const selected = checkbox.checked;
      const item = checkbox.closest('.bundle-offer__item');
      if (item) item.classList.toggle('is-selected', selected);
      if (!selected) return;
      selectedCount += 1;
      const price = parseFloat(checkbox.dataset.price || 0);
      const compare = parseFloat(checkbox.dataset.comparePrice || checkbox.dataset.compare || price);
      total += price;
      compareTotal += compare;
    });

    const discountedTotal = Math.max(0, total * totalPercentageLeft - totalFixedDiscount);
    if (totalPriceEl) totalPriceEl.textContent = formatMoney(discountedTotal, moneyFormat);
    if (totalCompareEl) {
      if (compareTotal > discountedTotal) {
        totalCompareEl.textContent = formatMoney(compareTotal, moneyFormat);
        totalCompareEl.hidden = false;
      } else {
        totalCompareEl.textContent = '';
        totalCompareEl.hidden = true;
      }
    }

    if (wrapper) {
      const labelEl = wrapper.querySelector('[data-bundle-label]');
      const countEl = wrapper.querySelector('[data-bundle-count]');
      const ctaBtn = wrapper.querySelector('[data-bundle-cta]');
      const bundleLabel = getBundleLabel(wrapper, selectedCount);
      if (labelEl) labelEl.textContent = bundleLabel;
      if (countEl) {
        countEl.textContent =
          selectedCount > 0
            ? `${selectedCount} item${selectedCount === 1 ? '' : 's'} selected`
            : wrapper.dataset.bundleCountEmpty || 'Choose 1, 2, or 3 items';
      }
      if (ctaBtn) {
        const prefix = wrapper.dataset.bundleCtaPrefix || 'Buy';
        const separator = wrapper.dataset.bundleCtaSeparator || ' - ';
        const defaultLabel = ctaBtn.dataset.defaultLabel || `${prefix} bundle`;
        ctaBtn.textContent =
          selectedCount > 0 ? `${prefix} ${selectedCount}${separator}${bundleLabel}` : defaultLabel;
        ctaBtn.disabled = selectedCount === 0;
      }
    }
  };

  const initBundleOfferSelections = (scope = document) => {
    const offers = scope.querySelectorAll('.bundle-offer-container');
    offers.forEach((offer) => {
      if (offer.dataset.bundleOfferInit === 'true') return;
      offer.dataset.bundleOfferInit = 'true';
      const checkboxes = offer.querySelectorAll('.bundle-deals__checkbox-js');
      checkboxes.forEach((checkbox) => {
        checkbox.addEventListener('change', () => updateBundleOfferTotals(offer));
      });
      updateBundleOfferTotals(offer);
    });
  };

  const initBundleOfferPickers = (scope = document) => {
    const pickers = scope.querySelectorAll('.bundle-deals__variant-selects-js');
    pickers.forEach((picker) => {
      if (picker.dataset.bundlePickerInit === 'true') return;
      picker.dataset.bundlePickerInit = 'true';
      const variantDataEl = picker.querySelector('script[type="application/json"]');
      if (!variantDataEl) return;
      const variants = JSON.parse(variantDataEl.textContent);
      const selects = Array.from(picker.querySelectorAll('select'));
      const moneyFormat = body.dataset.moneyFormat || '${{amount}}';

      const onChange = () => {
        const chosen = selects.map((select) => select.value);
        const variant = variants.find((item) => {
          if (item.options) {
            return item.options.every((opt, idx) => opt === chosen[idx]);
          }
          return chosen.every((val, idx) => val === item[`option${idx + 1}`]);
        });
        if (!variant) return;

        const card = picker.closest('.upsell, .bundle-deals__product');
        const checkbox = card ? card.querySelector('.bundle-deals__checkbox-js') : null;
        if (card) card.dataset.id = variant.id;
        if (checkbox) checkbox.dataset.id = variant.id;

        const image = card ? card.querySelector('.bundle-deals__media-item-container-js img') : null;
        if (image && variant.featured_image) {
          image.src = variant.featured_image.src.replace(/(\\.[a-z]+)(\\?.*)?$/i, '_500x$1');
          image.alt = variant.featured_image.alt || '';
        }

        const offer = card ? card.closest('[data-update-prices]') : null;
        const allowPriceUpdate = offer && offer.dataset.updatePrices === 'true';
        if (allowPriceUpdate) {
          const percLeft = parseFloat(card?.dataset.percentageLeft || 1);
          const fixedDis = parseFloat(card?.dataset.fixedDiscount || 0);
          const newPrice = variant.price * percLeft - fixedDis;
          const cmpPrice = variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price;
          const priceEl = card ? card.querySelector('.bundle-deals__price-js') : null;
          const compareEl = card ? card.querySelector('.bundle-deals__compare-price-js') : null;
          if (priceEl) priceEl.textContent = formatMoney(newPrice, moneyFormat);
          if (compareEl) compareEl.textContent = cmpPrice > newPrice ? formatMoney(cmpPrice, moneyFormat) : '';
          if (checkbox) {
            checkbox.dataset.price = newPrice;
            checkbox.dataset.comparePrice = cmpPrice;
          }
          if (card) {
            card.dataset.price = newPrice;
            card.dataset.comparePrice = cmpPrice;
          }
        }
        if (offer) updateBundleOfferTotals(offer);
      };

      selects.forEach((select) => select.addEventListener('change', onChange));
    });
  };

  const addBundleItems = async (items, trigger) => {
    if (!items.length) return;
    if (trigger) {
      trigger.disabled = true;
      trigger.classList.add('is-loading');
    }
    try {
      const response = await fetch(cartAddEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error('Bundle add failed');
      if (cartType === 'page') {
        window.location.href = cartUrl;
        return;
      }
      await refreshCartDrawer();
      const cart = await fetchCart();
      if (cart) updateCartCount(cart.item_count || 0);
      if (cartType === 'drawer') {
        openDrawerById('ReignCartDrawer');
      }
      if (cartType === 'notification') {
        showCartToast();
      }
    } catch (error) {
      console.error(error);
      alert('Could not add bundle');
    } finally {
      if (trigger) {
        trigger.disabled = false;
        trigger.classList.remove('is-loading');
      }
    }
  };

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-reign-atc], .reign-product__atc, .product-form__submit');
    if (!btn) return;
    const section = btn.closest('section[id^="MainProduct-"], .reign-product');
    if (!section) return;
    const mainBundleItems = section.querySelectorAll('.bundle-deals__checkbox-js[data-main-offer-item]');
    if (!mainBundleItems.length) return;
    const idInput = btn.closest('form')?.querySelector('input[name="id"]');
    const mainId = idInput ? Number(idInput.value) : null;
    if (!mainId) return;

    event.preventDefault();
    event.stopPropagation();

    const extras = Array.from(section.querySelectorAll('.bundle-deals__checkbox-js[data-main-offer-item]:checked'))
      .map((item) => ({ id: Number(item.dataset.id), quantity: 1 }))
      .filter((item) => item.id);
    const items = [{ id: mainId, quantity: 1 }, ...extras];
    addBundleItems(items, btn);
  });

  document.addEventListener('click', (event) => {
    const grabBtn = event.target.closest('[data-bundle-grab], .bundle-grab-btn, .bundle-offer .atc-button, bundle-deals .atc-button');
    if (!grabBtn) return;
    const bundle = grabBtn.closest('.bundle-offer, bundle-deals');
    if (!bundle) return;

    event.preventDefault();
    event.stopPropagation();

    const items = Array.from(bundle.querySelectorAll('.bundle-deals__checkbox-js:checked'))
      .map((item) => ({ id: Number(item.dataset.id), quantity: 1 }))
      .filter((item) => item.id);

    if (!items.length) {
      alert('Please choose a variant for each item.');
      return;
    }
    addBundleItems(items, grabBtn);
  });

  const productSections = document.querySelectorAll('[data-reign-product]');
  productSections.forEach((section) => {
    initProductSection(section);
  });

  const bindCartDrawerEvents = () => {
    if (!cartDrawer) return;
    if (cartDrawer.dataset.reignCartEventsInit === 'true') return;
    cartDrawer.dataset.reignCartEventsInit = 'true';
    const content = cartDrawer.querySelector('[data-reign-cart-drawer-content]');
    if (!content) return;

    const qtyButtons = content.querySelectorAll('[data-reign-cart-qty]');
    const qtyInputs = content.querySelectorAll('[data-reign-cart-input]');
    const removeLinks = content.querySelectorAll('[data-reign-cart-remove]');
    const noteInput = content.querySelector('[data-reign-cart-note]');

    qtyButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const step = Number(button.dataset.qtyStep || 0);
        const item = button.closest('[data-line]');
        const line = item ? item.dataset.lineKey || Number(item.dataset.line) : null;
        const input = item ? item.querySelector('[data-reign-cart-input]') : null;
        if (!line || !input) return;
        const current = Number(input.value || 0);
        const next = Math.max(0, current + step);
        input.value = next;
        queueCartLineUpdate(line, next);
      });
    });

    qtyInputs.forEach((input) => {
      const handler = () => {
        const line = input.dataset.lineKey || Number(input.dataset.line || 0);
        const quantity = input.value.trim() === '' ? NaN : Number(input.value);
        if (!line) return;
        queueCartLineUpdate(line, quantity);
      };
      input.addEventListener('change', handler);
    });

    removeLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const item = link.closest('[data-line]');
        const line = item ? item.dataset.lineKey || Number(item.dataset.line) : null;
        if (!line) return;
        updateCartLine(line, 0);
      });
    });

    if (noteInput) {
      noteInput.addEventListener('change', async () => {
        try {
          await fetch(cartUpdateEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ note: noteInput.value }),
          });
        } catch (error) {
          return;
        }
      });
    }

    bindDiscountButtons(content);
    bindShippingEstimate(content);
    bindAjaxAddForms(content);
  };

  const bindCartPageQuantity = () => {
    const sections = document.querySelectorAll('[data-reign-cart-section]');
    sections.forEach((section) => {
      if (section.dataset.reignCartQtyInit === 'true') return;
      section.dataset.reignCartQtyInit = 'true';
      const qtyButtons = section.querySelectorAll('[data-reign-cart-qty]');
      const qtyInputs = section.querySelectorAll('[data-reign-cart-input]');
      const removeLinks = section.querySelectorAll('[data-reign-cart-remove]');
      const noteInput = section.querySelector('[data-reign-cart-note]');
      qtyButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const step = Number(button.dataset.qtyStep || 0);
          const item = button.closest('[data-line]');
          const input = item ? item.querySelector('[data-reign-cart-input]') : null;
          const line = item ? item.dataset.lineKey || Number(item.dataset.line) : null;
          if (!input) return;
          const current = Number(input.value || 0);
          const next = Math.max(0, current + step);
          input.value = next;
          if (line) queueCartLineUpdate(line, next);
        });
      });

      qtyInputs.forEach((input) => {
        const handler = () => {
          const line = input.dataset.lineKey || Number(input.dataset.line || 0);
          const quantity = input.value.trim() === '' ? NaN : Number(input.value);
          if (!line) return;
          queueCartLineUpdate(line, quantity);
        };
        input.addEventListener('change', handler);
      });

      removeLinks.forEach((link) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          const item = link.closest('[data-line]');
          const line = item ? item.dataset.lineKey || Number(item.dataset.line) : null;
          if (!line) return;
          updateCartLine(line, 0);
        });
      });

      if (noteInput) {
        noteInput.addEventListener('change', async () => {
          try {
            await fetch(cartUpdateEndpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              body: JSON.stringify({ note: noteInput.value }),
            });
          } catch (error) {
            return;
          }
        });
      }
    });
  };

  const initAddressForms = () => {
    const addressForms = document.querySelectorAll('[data-reign-address-form]');
    addressForms.forEach((form) => {
      const countrySelects = form.querySelectorAll('[data-reign-country-select]');
      countrySelects.forEach((countrySelect) => {
        const provinceSelect = form.querySelector('[data-reign-province-select]');
        const provinceContainer = form.querySelector('[data-reign-province-container]');
        if (!provinceSelect || !provinceContainer) return;

        const setSelected = (select, value) => {
          if (!value) return;
          const match = Array.from(select.options).find(
            (option) => option.value === value || option.textContent === value
          );
          if (match) {
            match.selected = true;
          }
        };

        const updateProvinces = () => {
          const selectedOption = countrySelect.options[countrySelect.selectedIndex];
          let provinces = [];
          try {
            provinces = JSON.parse(selectedOption.dataset.provinces || '[]');
          } catch (error) {
            provinces = [];
          }

          provinceSelect.innerHTML = '';

          if (provinces.length) {
            provinces.forEach((province) => {
              const option = document.createElement('option');
              const primary = province[0] || '';
              const secondary = province[1] || '';
              const label =
                secondary && primary.length <= 3 && secondary.length > primary.length
                  ? secondary
                  : primary;
              option.value = label;
              option.textContent = label;
              provinceSelect.appendChild(option);
            });
            setSelected(provinceSelect, provinceSelect.dataset.default);
            provinceContainer.hidden = false;
          } else {
            provinceContainer.hidden = true;
          }
        };

        setSelected(countrySelect, countrySelect.dataset.default);
        updateProvinces();
        countrySelect.addEventListener('change', updateProvinces);
      });
    });
  };

  const bindAjaxAddForms = (scope = document) => {
    const forms = scope.querySelectorAll('form[action*="/cart/add"], form[data-reign-ajax-add]');
    forms.forEach((form) => {
      const buyNow = form.dataset.reignBuyNow === 'true';
      if (cartType === 'page' && !buyNow) return;
      if (form.dataset.reignAjaxBound) return;
      form.dataset.reignAjaxBound = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (form.dataset.reignSubmitting === 'true') return;
        const existingError = form.querySelector('[data-reign-cart-error]');
        if (existingError) existingError.remove();
        const productSection = form.closest('[data-reign-product]');
        const appendCartNotice = (message, showCartLink = false) => {
          const previousNotice = form.querySelector('[data-reign-cart-error]');
          if (previousNotice) previousNotice.remove();
          const notice = document.createElement('p');
          notice.setAttribute('data-reign-cart-error', '');
          notice.setAttribute('role', 'alert');
          notice.className = 'reign-cart-error';
          notice.tabIndex = -1;
          notice.textContent = message;
          if (showCartLink) {
            const link = document.createElement('a');
            link.href = cartUrl;
            link.textContent = 'View cart';
            notice.appendChild(document.createTextNode(' '));
            notice.appendChild(link);
          }
          form.appendChild(notice);
          // A sticky purchase can fail while the form is offscreen. Reveal its recovery action.
          notice.focus?.({ preventScroll: true });
          notice.scrollIntoView?.({ behavior: 'auto', block: 'center' });
        };
        if (cartLineUpdating || cartNeedsReload) {
          appendCartNotice(cartNeedsReload
            ? 'Please open your cart to confirm its contents before adding more items.'
            : 'Your cart is updating. Please try again in a moment.', cartNeedsReload);
          return;
        }
        form.dataset.reignSubmitting = 'true';
        const atcButtons = [];
        const mainAtc = form.querySelector('[data-reign-atc]');
        if (mainAtc) atcButtons.push(mainAtc);
        const stickyAtc = productSection ? productSection.querySelector('[data-reign-sticky-submit]') : null;
        if (stickyAtc) atcButtons.push(stickyAtc);
        atcButtons.forEach((button) => {
          if (!button) return;
          const label = button.querySelector('[data-reign-atc-label]');
          const price = button.querySelector('[data-reign-atc-price]');
          if (label) {
            if (!button.dataset.reignOriginalLabel) {
              button.dataset.reignOriginalLabel = label.textContent.trim();
            }
            label.textContent = button.dataset.reignLoadingText || 'Adding...';
          } else {
            if (!button.dataset.reignOriginalText) {
              button.dataset.reignOriginalText = button.textContent.trim();
            }
            button.textContent = button.dataset.reignLoadingText || 'Adding...';
          }
          if (price) {
            if (!button.dataset.reignOriginalPrice) {
              button.dataset.reignOriginalPrice = price.textContent.trim();
            }
            button.dataset.reignWasPriceHidden = price.hidden ? 'true' : 'false';
            price.hidden = true;
          }
          button.dataset.reignWasDisabled = button.disabled ? 'true' : 'false';
          button.disabled = true;
          button.setAttribute?.('aria-busy', 'true');
          button.classList.add('is-loading');
        });
        // FormData is captured once so the submitted variant and quantity cannot drift mid-request.
        const formData = new FormData(form);
        let addConfirmed = false;
        let navigatingToCheckout = false;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);
        try {
          const response = await fetch(cartAddEndpoint, {
            method: 'POST',
            signal: controller.signal,
            headers: { Accept: 'application/json' },
            body: formData,
          });
          if (!response.ok) {
            const details = await response.json().catch(() => null);
            const message = details?.description || details?.message;
            throw new Error(typeof message === 'string' ? message : 'This item could not be added. Please check availability and try again.');
          }
          addConfirmed = true;
          if (buyNow) {
            // Use Shopify checkout after one successful add, never a second add or a cart clear.
            window.location.assign(`${cartUrl.replace(/\/$/, '')}/checkout`);
            navigatingToCheckout = true;
            return;
          }
          const drawerRefreshed = await refreshCartDrawer(controller.signal);
          const cart = await fetchCart(controller.signal);
          if (cart) updateCartCount(cart.item_count || 0);
          if (cartType === 'drawer') {
            if (drawerRefreshed) {
              openDrawerById('ReignCartDrawer');
            } else {
              appendCartNotice('Added to cart, but the cart panel could not refresh.', true);
            }
          }
          if (cartType === 'notification') {
            showCartToast();
          }
        } catch (error) {
          // Never repeat a POST after an uncertain response: the cart may already contain the item.
          if (addConfirmed) {
            appendCartNotice('Added to cart, but the cart display could not refresh.', true);
          } else {
            appendCartNotice(
              error instanceof TypeError || error.name === 'AbortError'
                ? 'We could not confirm your cart update. Check your cart before trying again.'
                : error.message || 'Unable to add this item. Please try again.',
              error instanceof TypeError || error.name === 'AbortError'
            );
          }
        } finally {
          clearTimeout(timeout);
          const restorePurchaseState = () => {
            form.dataset.reignSubmitting = 'false';
            const canSyncProduct = productSection && typeof productSection.reignSyncPurchaseState === 'function';
            atcButtons.forEach((button) => {
              if (!button) return;
              button.classList.remove('is-loading');
              button.setAttribute?.('aria-busy', 'false');
              const label = button.querySelector('[data-reign-atc-label]');
              const price = button.querySelector('[data-reign-atc-price]');
              if (!canSyncProduct) {
                const wasDisabled = button.dataset.reignWasDisabled === 'true';
                button.disabled = wasDisabled;
                if (label && button.dataset.reignOriginalLabel) {
                  label.textContent = button.dataset.reignOriginalLabel;
                } else if (!label && button.dataset.reignOriginalText) {
                  button.textContent = button.dataset.reignOriginalText;
                }
              }
              if (price && !canSyncProduct) {
                if (button.dataset.reignOriginalPrice) {
                  price.textContent = button.dataset.reignOriginalPrice;
                }
                const wasHidden = button.dataset.reignWasPriceHidden === 'true';
                price.hidden = wasHidden;
              }
              delete button.dataset.reignWasDisabled;
              delete button.dataset.reignOriginalLabel;
              delete button.dataset.reignOriginalPrice;
              delete button.dataset.reignWasPriceHidden;
              delete button.dataset.reignOriginalText;
            });
            if (canSyncProduct) {
              productSection.reignSyncPurchaseState();
            }
          };
          if (navigatingToCheckout && typeof window.addEventListener === 'function') {
            // Back navigation may restore the page from memory with its loading state intact.
            window.addEventListener('pageshow', restorePurchaseState, { once: true });
          } else {
            restorePurchaseState();
          }
        }
      });
    });
  };

  const initTenLoveFinishSelectors = (scope = document) => {
    const buttons = scope.querySelectorAll('[data-ten-love-finish-select]');
    buttons.forEach((button) => {
      if (button.dataset.tenLoveFinishBound === 'true') return;
      button.dataset.tenLoveFinishBound = 'true';

      button.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const requestedValue = (button.dataset.tenLoveFinishSelect || '').trim().toLowerCase();
        const productSection = document.querySelector('[data-reign-product]');
        if (!productSection) return;
        const linkedProduct = button.closest('[data-style-product]')?.dataset.styleProduct;
        if (linkedProduct && linkedProduct !== productSection.dataset.productId) return;

        const matchingInput = Array.from(
          productSection.querySelectorAll('[data-reign-option]')
        ).find((input) => input.value.trim().toLowerCase() === requestedValue);

        if (matchingInput && !matchingInput.disabled) {
          event.preventDefault();
          matchingInput.click();
          const variantPicker = productSection.querySelector('.reign-product__form') || productSection;
          variantPicker.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        }
      });
    });
  };

  const initBundles = (scope = document) => {
    bindBundleDeals(scope);
    initBundlePurchaseToggle(scope);
    initBundleOfferPickers(scope);
    initBundleOfferSelections(scope);
  };

  const initPress = (scope = document) => {
    initPressCarousels(scope);
  };

  const initCartFeatures = (scope = document) => {
    bindAjaxAddForms(scope);
    bindCartDrawerEvents();
    bindCartPageQuantity();
  };

  document.addEventListener('shopify:section:load', (event) => {
    const scope = event && event.target ? event.target : document;
    if (activePalette) {
      applyPaletteToScope(activePalette, scope);
    }
    initModals(scope);
    initModalTriggers(scope);
    initPredictiveSearch(scope);
    scope.querySelectorAll('[data-reign-product]').forEach((section) => {
      initProductSection(section);
    });
    initPress(scope);
    initAnnouncementBars(scope);
    bindShippingEstimate(scope);
    bindShareButtons(scope);
    bindCopyButtons(scope);
    initBundles(scope);
    initCartFeatures(scope);
    initAnimations(scope);
    initFeaturedProductGallery(scope);
    initTenLoveFinishSelectors(scope);
  });

  initCartFeatures();
  bindDiscountButtons();
  bindShippingEstimate();
  bindShareButtons();
  bindCopyButtons();
  initPredictiveSearch();
  initBundles();
  initPress();
  initAnnouncementBars();
  bindCartDrawerEvents();
  bindCartPageQuantity();
  initAddressForms();
  initAnimations();
  initFeaturedProductGallery();
  initTenLoveFinishSelectors();
})();
